import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import Seo from '../components/Seo';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Message sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'CMpeppersofficial@gmail.com',
      link: 'mailto:CMpeppersofficial@gmail.com'
    },
    {
      icon: Phone,
      title: 'Phone',
      value: '(562) 292-1095',
      link: 'tel:+15622921095'
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Research Facility, United States',
      link: null
    }
  ];

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: "#0C0A09" }}>
      <Seo
        path="/contact"
        title="Contact Eman's Peppers | Text or Email Us"
        description="Get in touch with Eman's Peppers — text, email, or reach out on Discord for questions about products, orders, or local Norwalk, CA pickup."
      />
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-[#F7F3EE] mb-4">
            Get in Touch
          </h1>
          <p className="text-xl text-[#B5A79E]">
            Have questions? We're here to help. Reach out to our team.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-100 text-teal-600 rounded-lg">
                        <info.icon className="h-6 w-6" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#F7F3EE] mb-1">{info.title}</h3>
                      {info.link ? (
                        <a 
                          href={info.link} 
                          className="text-[#B5A79E] hover:text-teal-600 transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-[#B5A79E]">{info.value}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Business Hours */}
            <Card className="border-none shadow-lg bg-gradient-to-br from-teal-600 to-emerald-600 text-white">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 text-lg">Business Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>9AM - 6PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span>10AM - 4PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#F7F3EE] mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#B5A79E] mb-2">
                        Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5A79E] mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#B5A79E] mb-2">
                        Phone
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(562) 292-1095"
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#B5A79E] mb-2">
                        Subject *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="How can we help?"
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#B5A79E] mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us more about your inquiry..."
                      rows={6}
                      className="resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#D4A373] hover:bg-[#E6CC98] text-white h-12 text-lg"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Discord Community Section */}
        <div className="max-w-4xl mx-auto mt-16">
          <div 
            className="p-8 rounded-lg text-center"
            style={{ 
              backgroundColor: '#1A1513',
              border: '2px solid #5865F2',
              boxShadow: '0 10px 40px rgba(88, 101, 242, 0.3)'
            }}
          >
            <div className="flex justify-center mb-6">
              <svg className="w-16 h-16" viewBox="0 0 71 55" fill="#5865F2">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
              </svg>
            </div>
            
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#F7F3EE', fontFamily: 'Playfair Display, serif' }}>
              Join Our Discord Community
            </h2>
            
            <p className="text-lg mb-2" style={{ color: '#B5A79E' }}>
              Connect with fellow researchers and get <span style={{ color: '#D4A373', fontWeight: '600' }}>exclusive access</span> to:
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center my-6">
              <div className="text-sm" style={{ color: '#B5A79E' }}>
                ✨ Secret flash sales <span className="text-2xl">🤫</span>
              </div>
              <div className="text-sm" style={{ color: '#B5A79E' }}>
                💬 Community support
              </div>
              <div className="text-sm" style={{ color: '#B5A79E' }}>
                📢 Early product drops
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center justify-center mt-8">
              {/* QR Code */}
              <div className="text-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cm-peptides/artifacts/4fqjmxsn_IMG_5206.jpeg"
                  alt="Discord QR Code"
                  className="w-48 h-48 rounded-lg"
                  style={{ border: '3px solid #5865F2' }}
                />
                <p className="text-xs mt-2" style={{ color: '#B5A79E' }}>Scan to join</p>
              </div>

              {/* Or Divider */}
              <div className="text-2xl font-bold" style={{ color: '#B5A79E' }}>OR</div>

              {/* Join Button */}
              <div className="text-center">
                <a 
                  href="https://discord.gg/9NfmAQusF" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-4 rounded text-lg font-bold transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: '#5865F2',
                    color: '#ffffff',
                    boxShadow: '0 8px 20px rgba(88, 101, 242, 0.4)'
                  }}
                >
                  Join Discord Server
                </a>
                <p className="text-xs mt-3" style={{ color: '#B5A79E' }}>
                  Click to join instantly
                </p>
              </div>
            </div>

            <p className="text-xs mt-6" style={{ color: '#B5A79E', opacity: 0.7 }}>
              *Exclusive deals and early access only available to Discord members
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
